// ProductsFilter.tsx
"use client";
import { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { X } from "lucide-react";
import { Accordion } from "@/app/components/shadcn/accordion";
import {
  FilterListGroup,
  FilterInputGroup,
} from "@/app/lib/features/filters/filters.types";
import useFilters from "@/app/lib/features/filters/filters.hook";
import useGenericProducts from "@/app/lib/features/genericProducts/genericProducts.hook";
import FilterHeaderSection from "./FilterHeaderSection";
import FilterListSection from "./FilterListSection";
import FilterPriceSection from "./FilterPriceSection";
import FilterSkeleton from "@/app/components/elements/skeletons/FilterSkeleton";

const FILTER_PARAM_KEYS = [
  "gender", "category", "category_groups", "category_sub_groups",
  "color", "size", "brand", "season", "seller_badge", "rating",
  "discounted", "new_products", "coupon", "bulk_sale", "is_advantageous",
  "flash_products", "min_price", "max_price", "sort", "special_filters",
];

const FilterClearButton = ({ onClear }: { onClear?: () => void }) => {
  const searchParams = useSearchParams();
  const [isClearing, setIsClearing] = useState(false);

  const hasActiveFilters = FILTER_PARAM_KEYS.some((key) => searchParams.has(key));

  useEffect(() => {
    if (!hasActiveFilters) setIsClearing(false);
  }, [hasActiveFilters]);

  if (!hasActiveFilters && !isClearing) return null;
  if (!onClear) return null;

  return (
    <div className="flex justify-end mb-2">
      <button
        onClick={() => { setIsClearing(true); onClear(); }}
        disabled={isClearing}
        className="cursor-pointer flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-500 border border-gray-200 hover:border-red-300 rounded-full px-3 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <X size={12} />
        Filtreleri Temizle
      </button>
    </div>
  );
};

const ProductsFilter = ({ onClearFilters, disableMinimumFilter }: { onClearFilters?: () => void; disableMinimumFilter?: boolean }) => {
  const { headerFilters, generalFilters, isLoading, isIdle } = useFilters();
  const { isLoading: isProductsLoading } = useGenericProducts();

  // Checked (selected_count > 0) olan tüm gruplar açık başlar.
  const defaultOpenValues = generalFilters
    .filter((f) => f.selected_count > 0)
    .map((f) => f.key);

  // Slug değişiminde (component reuse) Accordion remount olsun diye key —
  // aksi halde uncontrolled defaultValue güncellenmez, önceki slug'ın açık grupları kalır.
  const accordionKey = defaultOpenValues.join(",");

  // Yalnızca veri hiç yokken skeleton göster — filtre değişiminde mevcut liste kalır
  if (
    (isLoading || isIdle) &&
    !headerFilters.length &&
    !generalFilters.length
  ) {
    return (
      <aside className="w-full sticky top-32 max-h-[calc(100vh-136px)] overflow-y-auto overscroll-contain">
        <FilterSkeleton />
      </aside>
    );
  }

  return (
    <aside
      className="w-full sticky top-32 max-h-[calc(100vh-136px)] overflow-y-auto overscroll-contain"
      style={
        isProductsLoading ? { pointerEvents: "none", opacity: 0.6 } : undefined
      }
    >
      <Suspense fallback={null}>
        <FilterClearButton onClear={onClearFilters} />
      </Suspense>
      <FilterHeaderSection items={headerFilters} />
      <Accordion
        key={accordionKey}
        type="multiple"
        defaultValue={defaultOpenValues}
      >
        {generalFilters.map((filter) => {
          if (filter.type === "input") {
            return (
              <FilterPriceSection
                key={filter.key}
                filter={filter as FilterInputGroup}
              />
            );
          }
          return (
            <FilterListSection
              key={filter.key}
              filter={filter as FilterListGroup}
              disableMinimumFilter={disableMinimumFilter}
            />
          );
        })}
      </Accordion>
    </aside>
  );
};

export default ProductsFilter;
